import { useEffect } from "react";
import {
  resolveTheme,
  usePreferencesStore,
  type ThemePreference,
} from "@/shared/stores/preferencesStore";

/**
 * Applique le thème choisi à la racine du document.
 *
 * ## Ce que ce hook pose sur `<html>`
 * - `class="dark"` — ce que lit Tailwind (`darkMode: "class"`) ;
 * - `data-theme="dark|light"` — utilisable en CSS pur, sans dépendre de Tailwind.
 *
 * Les deux disent la même chose. Le doublon est volontaire : les variables CSS
 * de la palette peuvent cibler `[data-theme]` sans que Tailwind soit dans la
 * boucle, ce qui garde la feuille de style lisible pour qui ne connaît pas la
 * convention de Tailwind.
 *
 * ## `color-scheme`, souvent oublié
 * Il ne colore rien de l'app, mais il indique au NAVIGATEUR de basculer ses
 * propres éléments : barres de défilement, champs de formulaire natifs,
 * sélecteurs de date, autofill. Sans lui, on obtient des barres blanches
 * éclatantes le long d'une interface sombre.
 *
 * ## Suivi du réglage système
 * Tant que la préférence vaut `system`, un changement côté appareil (bascule
 * automatique au coucher du soleil) doit se voir immédiatement, sans
 * rechargement — d'où l'écoute de `prefers-color-scheme`.
 */
export function useApplyTheme(): void {
  const theme = usePreferencesStore((s) => s.theme);

  useEffect(() => {
    applyTheme(theme);

    // Seul le mode `system` suit l'appareil : un choix explicite ne doit JAMAIS
    // être écrasé par un changement de réglage système.
    if (theme !== "system") return;
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme("system");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [theme]);
}

/**
 * Pose le thème sur `<html>`. Exporté à part du hook pour être appelable AVANT
 * le premier rendu de React (cf. `main.tsx`) : appliqué seulement dans un
 * effet, le thème arriverait après la première peinture et produirait un flash
 * blanc à chaque chargement.
 */
export function applyTheme(preference: ThemePreference): void {
  if (typeof document === "undefined") return;

  const resolved = resolveTheme(preference);
  const root = document.documentElement;

  root.classList.toggle("dark", resolved === "dark");
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;

  // `theme-color` teinte la barre d'adresse et la barre système sur mobile.
  // Figée dans index.html, elle restait navy quel que soit le thème : sur un
  // fond d'app clair, la barre paraissait appartenir à une autre application.
  // On la lit depuis la palette pour qu'elle colle TOUJOURS au canevas réel.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const canvas = getComputedStyle(root).getPropertyValue("--c-white").trim();
    if (canvas) meta.setAttribute("content", canvas);
  }
}
